package com.evoapps.evoshift.domain.spreadsheet

/**
 * Reads a picked, dropped or pasted spreadsheet ([Spreadsheet]); the entry point the imports use
 * (SH-123 sales, SH-214 staff, SH-217 rosters). What the bytes are decides the format, not the
 * file name: a zip is an .xlsx, an OLE2 compound file is the old .xls (or an encrypted workbook,
 * which Excel stores the same way), anything else is CSV text.
 */
object SpreadsheetReader {
    /** The largest file read (about 5 MB): a staff list or a year of sales is far smaller. */
    const val MAX_FILE_BYTES = 5 * 1024 * 1024
    /** The most rows a sheet may have. */
    const val MAX_ROWS = 5000

    /** One byte over [MAX_FILE_BYTES]: what a file picker reads at most to know a file is too large. */
    const val READ_LIMIT = MAX_FILE_BYTES + 1

    /** Fails with [SpreadsheetException] when the file cannot be read (see [SpreadsheetProblem]). */
    suspend fun read(bytes: ByteArray, maxRows: Int = MAX_ROWS): Spreadsheet {
        if (bytes.isEmpty()) throw SpreadsheetException(SpreadsheetProblem.EMPTY)
        if (bytes.size > MAX_FILE_BYTES) throw SpreadsheetException(SpreadsheetProblem.TOO_LARGE)
        val spreadsheet = try {
            when {
                ZipReader.looksLikeZip(bytes) -> XlsxReader.read(bytes, maxRows)
                isCompoundFile(bytes) -> throw SpreadsheetException(
                    if (isEncryptedPackage(bytes)) SpreadsheetProblem.PASSWORD_PROTECTED else SpreadsheetProblem.LEGACY_XLS
                )
                else -> Spreadsheet(SpreadsheetFormat.CSV, listOf(CsvReader.read(bytes, maxRows)))
            }
        } catch (e: SpreadsheetException) {
            throw e
        } catch (e: Exception) {
            // Whatever a damaged workbook made the reader trip over.
            throw SpreadsheetException(SpreadsheetProblem.CORRUPT, e.message)
        }
        if (spreadsheet.sheets.all { it.isBlank }) throw SpreadsheetException(SpreadsheetProblem.EMPTY)
        return spreadsheet
    }

    /** Cells pasted from Excel or Google Sheets (tab separated; a pasted CSV text works too). */
    fun readPasted(text: String, maxRows: Int = MAX_ROWS): Spreadsheet {
        val sheet = Sheet("", CsvReader.rows(text, maxRows))
        if (sheet.isBlank) throw SpreadsheetException(SpreadsheetProblem.EMPTY)
        return Spreadsheet(SpreadsheetFormat.PASTE, listOf(sheet))
    }

    /** The OLE2 compound file signature: .xls, .doc, and Office files encrypted with a password. */
    private val COMPOUND_SIGNATURE = byteArrayOf(0xD0.toByte(), 0xCF.toByte(), 0x11, 0xE0.toByte(), 0xA1.toByte(), 0xB1.toByte(), 0x1A, 0xE1.toByte())

    private fun isCompoundFile(bytes: ByteArray): Boolean =
        bytes.size >= COMPOUND_SIGNATURE.size && COMPOUND_SIGNATURE.indices.all { bytes[it] == COMPOUND_SIGNATURE[it] }

    /** An encrypted .xlsx is a compound file with an "EncryptedPackage" stream (its name in UTF-16LE). */
    private fun isEncryptedPackage(bytes: ByteArray): Boolean {
        val name = "EncryptedPackage".flatMap { listOf(it.code.toByte(), 0) }.toByteArray()
        outer@ for (i in 0..bytes.size - name.size) {
            for (j in name.indices) if (bytes[i + j] != name[j]) continue@outer
            return true
        }
        return false
    }
}
